import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { usePrevious } from '@client/hooks/use-utils.hooks';
import { IconButton } from '@ui/buttons';
import { SidePanel } from '@ui/layout';
import { Slider, SliderProps, useSliderState } from '@ui/Slider';
import { UnitNumberInput } from '@ui/UnitNumberInput';
import { is } from '@client/helpers/dom.guards';
import { roundToDecimals } from '@shared/helpers/math.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';

export type PropertySliderData = {
  mixed: boolean;
  current: { average: number; mixed: boolean };
  fallback: { average: number; mixed: boolean };
  hasExplicitValue: boolean;
  hasFallbackValue: boolean;
  hasCompatibleItems: boolean;
};

export type PropertySliderSectionProps = {
  label: string;
  data: PropertySliderData;
  onCommit: (value?: number) => void;
  /**
   * Value at which the slider is considered "inactive" and the Add button is shown.
   */
  inactiveValue?: number;
  min?: number;
  max?: number;
  /**
   * Shown when the stored expression is outside min/max even if the displayed
   * number is still inside those bounds.
   */
  errorMessage?: string;
  /**
   * When this key changes, editing state and slider value are reset.
   * Use to sync with parent scope changes (e.g. element selection).
   */
  resetKey?: string;
  /**
   * Called when the form (slider and input) is shown by clicking the "plus" button.
   */
  onShowForm?: () => void;
  /**
   * When true, skips {@link SidePanel.Content.Section} horizontal padding so the row
   * aligns with property lists inside accordion panels.
   */
  embedded?: boolean;
};

export const PropertySliderSection: React.FC<PropertySliderSectionProps> = ({
  label,
  data,
  onCommit,
  inactiveValue = 0,
  min = 0,
  max = 1,
  errorMessage,
  resetKey,
  onShowForm,
  embedded = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const slider = useSliderState({
    value: data.current.average,
    onCommit,
  });

  const previousResetKey = usePrevious(resetKey, { setInitial: true });
  if (previousResetKey !== resetKey) {
    setIsEditing(false);
    slider.setValue(data.current.average);
  }

  const showInputForm =
    data.hasCompatibleItems &&
    (isEditing || slider.isDragging || slider.value !== inactiveValue);
  // Property may not exist yet — still show label + add (onShowForm creates the row)
  const showAddButton = !showInputForm || !data.hasCompatibleItems;

  const handleShowForm = useCallback(() => {
    onShowForm?.();
    setIsEditing(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  }, [onShowForm]);

  const handleSectionBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (slider.isDragging) return;
      const nextTarget = event.relatedTarget;
      if (is.Node(nextTarget) && sectionRef.current?.contains(nextTarget)) {
        return;
      }
      setIsEditing(false);
    },
    [slider.isDragging],
  );

  const handleSliderChange = useCallback<NonNullable<SliderProps['onChange']>>(
    (_event, value) => {
      slider.onChange(_event, roundToDecimals(value, 2));
    },
    [slider],
  );

  const handleInputCommit = useCallback(
    (value?: number) => {
      if (!guards.is.number(value)) {
        slider.setValue(data.current.average);
        onCommit(undefined);
        return;
      }
      const next = roundToDecimals(value, 2);
      slider.setValue(next);
      onCommit(next);
    },
    [onCommit, data, slider],
  );

  const inputValue = (() => {
    if (slider.isDragging) return slider.value;
    if (!data.hasExplicitValue) return undefined;
    if (data.current.mixed) return undefined;
    return data.current.average;
  })();

  const row = (
    <>
      <Box
        sx={{
          display: 'grid',
          alignItems: 'center',
          gridTemplateColumns: '1fr 1fr',
          gap: 1,
          width: '100%',
          minHeight: embedded ? 40 : undefined,
          py: embedded ? 0 : 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2">{label}</Typography>
        </Box>
        {showAddButton ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <IconButton category="mui" icon="Add" onClick={handleShowForm} />
          </Box>
        ) : (
          <UnitNumberInput
            unit="%"
            size="small"
            outlineVisibility="hover"
            unitAlignment="none"
            placeholder={data.mixed ? 'mixed' : undefined}
            fallback={
              !data.mixed && data.hasFallbackValue
                ? data.fallback.average
                : undefined
            }
            value={inputValue}
            min={min}
            max={max}
            errorMessage={errorMessage}
            inputRef={inputRef}
            onChange={handleInputCommit}
          />
        )}
      </Box>
      {showInputForm && (
        <Slider
          min={min * 100}
          max={max * 100}
          value={slider.value * 100}
          onChange={(e, value, activeThumb) =>
            handleSliderChange(e, value / 100, activeThumb)
          }
          onChangeCommitted={(event, value) =>
            slider.onChangeCommitted(event, value / 100)
          }
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <Box
        ref={sectionRef}
        onBlur={handleSectionBlur}
        sx={{
          width: '100%',
        }}
      >
        {row}
      </Box>
    );
  }

  return (
    <SidePanel.Content.Section ref={sectionRef} onBlur={handleSectionBlur}>
      {row}
    </SidePanel.Content.Section>
  );
};
